/**
 * Overcut — page d'accueil. Premier module : le « garage », un manège 3D de broderies.
 *
 * Sans JavaScript, le garage est une simple rangée de liens qui défile au doigt.
 * Ce script la transforme en manège : chaque voiture est placée sur un cercle
 * vu en perspective, et le cercle tourne tout seul, au doigt, à la souris, au
 * clavier et avec les flèches.
 *
 * Toucher la voiture de face ouvre sa collection. Toucher une voiture de côté
 * l'amène d'abord de face : sur mobile, un lien pris au vol pendant que le
 * manège tourne serait un clic par erreur.
 *
 * « Réduire les animations » : pas de rotation automatique, pas d'inertie,
 * le manège ne bouge que d'un cran à la fois, sans transition.
 */
(() => {
  if (customElements.get('overcut-garage')) return;

  const reduit = window.matchMedia('(prefers-reduced-motion: reduce)');
  const TOUR = Math.PI * 2;

  class OvercutGarage extends HTMLElement {
    connectedCallback() {
      this.voitures = [...this.querySelectorAll('[data-garage-voiture]')];
      if (this.voitures.length < 3) return;

      this.n = this.voitures.length;
      this.pas = TOUR / this.n;
      this.angle = 0; // angle affiché
      this.cible = 0; // angle visé, vers lequel l'affiché glisse
      this.vitesse = 0;
      this.inclinaison = { x: 0, y: 0 };
      this.actif = -1;
      this.visible = true;
      this.delai = parseFloat(this.dataset.delai) || 3200;

      this.scene = this.querySelector('[data-garage-scene]');
      this.sol = this.querySelector('[data-garage-sol]');
      this.nom = this.querySelector('[data-garage-nom]');
      this.infos = this.querySelector('[data-garage-infos]');
      this.lien = this.querySelector('[data-garage-lien]');
      this.points = [...this.querySelectorAll('[data-garage-point]')];
      this.rang = this.querySelector('[data-garage-rang]');

      this.classList.add('is-3d');
      this.mesurer();
      this.brancher();
      this.mettreAJour(true);
      this.relancer();
      this.boucle = requestAnimationFrame((t) => this.image(t));
    }

    disconnectedCallback() {
      cancelAnimationFrame(this.boucle);
      clearTimeout(this.minuteur);
      this.observateur?.disconnect();
      this.redim?.disconnect();
      window.removeEventListener('deviceorientation', this.surGyro);
    }

    /* ---------- Géométrie ---------- */

    mesurer() {
      const largeur = this.scene.clientWidth;
      // La voiture de face occupe ~70 % de la scène ; le rayon écarte ses
      // voisines juste assez pour qu'elles dépassent sur les bords sans la
      // chevaucher, ce qui donne la profondeur sans rien cacher.
      this.largeurVoiture = Math.min(largeur * 0.72, 380);
      this.rayon = this.largeurVoiture * 1.3;
      this.style.setProperty('--garage-voiture', `${this.largeurVoiture.toFixed(0)}px`);
    }

    placer() {
      const r = this.rayon;
      for (let i = 0; i < this.n; i++) {
        const theta = i * this.pas + this.angle;
        const sin = Math.sin(theta);
        const cos = Math.cos(theta); // 1 = de face, -1 = au fond
        const x = sin * r;
        const z = (cos - 1) * r; // la voiture de face reste à z = 0
        const proche = (cos + 1) / 2; // 0 au fond, 1 de face
        const el = this.voitures[i];
        el.style.transform =
          `translate(-50%, -50%) translate3d(${x.toFixed(1)}px, ${(-(1 - proche) * 18).toFixed(1)}px, ${z.toFixed(1)}px) ` +
          `rotateY(${((theta * 180) / Math.PI).toFixed(2)}deg)`;
        el.style.opacity = (0.18 + proche * 0.82).toFixed(3);
        el.style.zIndex = String(Math.round(proche * 100));
        el.style.filter = proche > 0.92 ? 'none' : `brightness(${(0.45 + proche * 0.55).toFixed(2)})`;
      }
      this.scene.style.transform =
        `rotateX(${(6 + this.inclinaison.x).toFixed(2)}deg) rotateY(${this.inclinaison.y.toFixed(2)}deg)`;
      if (this.sol) this.sol.style.transform = `rotateX(78deg) rotateZ(${((this.angle * 180) / Math.PI).toFixed(2)}deg)`;
    }

    indiceDeFace() {
      // L'angle qui ramène la voiture i de face est -i * pas.
      const brut = Math.round(-this.angle / this.pas);
      return ((brut % this.n) + this.n) % this.n;
    }

    /* ---------- Animation ---------- */

    image(t) {
      this.boucle = requestAnimationFrame((u) => this.image(u));
      if (!this.visible || document.hidden) return;

      if (!this.glisse) {
        if (reduit.matches) {
          this.angle = this.cible;
        } else {
          // Ressort amorti vers la cible : rapide, sans rebond visible.
          const ecart = this.cible - this.angle;
          this.vitesse = this.vitesse * 0.78 + ecart * 0.075;
          this.angle += this.vitesse;
          if (Math.abs(ecart) < 0.0004 && Math.abs(this.vitesse) < 0.0004) {
            this.angle = this.cible;
            this.vitesse = 0;
          }
        }
      }

      // Retour doux de l'inclinaison quand on lâche.
      if (!this.incline) {
        this.inclinaison.x *= 0.92;
        this.inclinaison.y *= 0.92;
      }

      this.placer();
      this.mettreAJour();
    }

    allerA(indice) {
      // Chemin le plus court vers la voiture demandée.
      const actuel = -this.cible / this.pas;
      let ecart = (((indice - actuel) % this.n) + this.n) % this.n;
      if (ecart > this.n / 2) ecart -= this.n;
      this.cible = -(actuel + ecart) * this.pas;
      this.relancer();
    }

    avancer(sens) {
      this.cible = (Math.round(this.cible / this.pas) - sens) * this.pas;
      this.relancer();
    }

    relancer() {
      clearTimeout(this.minuteur);
      if (reduit.matches || this.hasAttribute('data-pause')) return;
      this.minuteur = setTimeout(() => {
        if (this.visible && !document.hidden && !this.glisse) this.avancer(1);
        else this.relancer();
      }, this.delai);
    }

    mettreAJour(force) {
      const i = this.indiceDeFace();
      if (i === this.actif && !force) return;
      this.actif = i;
      const el = this.voitures[i];
      this.voitures.forEach((v, j) => {
        v.classList.toggle('is-face', j === i);
        // Seule la voiture de face reste dans l'ordre de tabulation.
        v.tabIndex = j === i ? 0 : -1;
        v.setAttribute('aria-hidden', j === i ? 'false' : 'true');
      });
      this.points.forEach((p, j) => p.setAttribute('aria-current', j === i ? 'true' : 'false'));
      if (this.rang) this.rang.textContent = String(i + 1).padStart(2, '0');
      if (this.nom) this.nom.textContent = el.dataset.nom;
      if (this.infos) this.infos.textContent = el.dataset.infos;
      if (this.lien) {
        this.lien.href = el.href;
        // La voiture personnalisée a son propre libellé (« Envoyer ma photo »).
        const libelle = el.dataset.bouton || this.lien.dataset.libelle;
        const texte = this.lien.querySelector('[data-garage-lien-texte]');
        if (texte) texte.textContent = libelle;
        this.lien.setAttribute('aria-label', `${libelle} : ${el.dataset.nom}`);
      }
      // Relance l'apparition du nom à chaque changement.
      const carte = this.querySelector('[data-garage-carte]');
      if (carte && !reduit.matches) {
        carte.classList.remove('is-nouveau');
        void carte.offsetWidth;
        carte.classList.add('is-nouveau');
      }
    }

    /* ---------- Interactions ---------- */

    brancher() {
      const zone = this.querySelector('[data-garage-zone]') || this;
      let depart = null;

      zone.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        depart = { x: e.clientX, y: e.clientY, angle: this.angle, t: performance.now(), dernierX: e.clientX, v: 0 };
        this.bouge = false;
      });

      zone.addEventListener('pointermove', (e) => {
        if (e.pointerType === 'mouse' && !depart) {
          // Souris au survol : la scène suit légèrement le curseur.
          const r = zone.getBoundingClientRect();
          this.incline = true;
          this.inclinaison.y = ((e.clientX - r.left) / r.width - 0.5) * 10;
          this.inclinaison.x = -((e.clientY - r.top) / r.height - 0.5) * 8;
          return;
        }
        if (!depart) return;
        const dx = e.clientX - depart.x;
        const dy = e.clientY - depart.y;
        if (!this.glisse) {
          // Geste vertical : on laisse la page défiler.
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 8) {
            depart = null;
            return;
          }
          if (Math.abs(dx) < 8) return;
          this.glisse = true;
          this.bouge = true;
          clearTimeout(this.minuteur);
          zone.setPointerCapture?.(e.pointerId);
        }
        const maintenant = performance.now();
        const dt = Math.max(1, maintenant - depart.t);
        depart.v = (e.clientX - depart.dernierX) / dt;
        depart.dernierX = e.clientX;
        depart.t = maintenant;
        // Un glissement de la largeur d'une voiture = un cran.
        this.angle = depart.angle + (dx / this.largeurVoiture) * this.pas;
        this.cible = this.angle;
      });

      const lacher = () => {
        if (this.glisse) {
          const elan = reduit.matches ? 0 : depart?.v * 220 || 0;
          const projete = this.angle + (elan / this.largeurVoiture) * this.pas;
          this.cible = Math.round(projete / this.pas) * this.pas;
          this.vitesse = 0;
          this.relancer();
        }
        this.glisse = false;
        depart = null;
      };
      zone.addEventListener('pointerup', lacher);
      zone.addEventListener('pointercancel', lacher);
      zone.addEventListener('pointerleave', () => {
        this.incline = false;
      });

      // Ce touch-action laisse le défilement vertical à la page.
      zone.style.touchAction = 'pan-y';

      this.voitures.forEach((v, i) => {
        v.addEventListener('click', (e) => {
          if (this.bouge) {
            e.preventDefault();
            this.bouge = false;
            return;
          }
          if (i !== this.actif) {
            e.preventDefault();
            this.allerA(i);
          }
        });
        v.addEventListener('dragstart', (e) => e.preventDefault());
      });

      this.querySelector('[data-garage-prec]')?.addEventListener('click', () => this.avancer(-1));
      this.querySelector('[data-garage-suiv]')?.addEventListener('click', () => this.avancer(1));
      this.points.forEach((p, i) => p.addEventListener('click', () => this.allerA(i)));

      this.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') this.avancer(1);
        else if (e.key === 'ArrowLeft') this.avancer(-1);
        else return;
        e.preventDefault();
        // Le focus suit la voiture qui arrive de face.
        requestAnimationFrame(() => {
          if (this.contains(document.activeElement) && document.activeElement.matches('[data-garage-voiture]')) {
            this.voitures[this.indiceDeFace()]?.focus({ preventScroll: true });
          }
        });
      });

      // Survol ou focus clavier : on arrête le tour automatique.
      this.addEventListener('focusin', () => this.setAttribute('data-pause', ''));
      this.addEventListener('focusout', () => {
        this.removeAttribute('data-pause');
        this.relancer();
      });

      // Hors écran : rien ne tourne, rien ne se calcule.
      this.observateur = new IntersectionObserver(([entree]) => {
        this.visible = entree.isIntersecting;
        if (this.visible) this.relancer();
      });
      this.observateur.observe(this);

      this.redim = new ResizeObserver(() => this.mesurer());
      this.redim.observe(this.scene);

      // Téléphone incliné : la scène suit, très légèrement. iOS exige une
      // autorisation explicite ; on ne la demande pas pour un simple effet.
      if (!reduit.matches && 'DeviceOrientationEvent' in window && typeof DeviceOrientationEvent.requestPermission !== 'function') {
        this.surGyro = (e) => {
          if (e.gamma == null || this.incline) return;
          this.inclinaison.y = Math.max(-8, Math.min(8, e.gamma / 4));
          this.inclinaison.x = Math.max(-6, Math.min(6, ((e.beta || 45) - 45) / 6));
        };
        window.addEventListener('deviceorientation', this.surGyro, { passive: true });
      }
    }
  }

  customElements.define('overcut-garage', OvercutGarage);
})();
